import React, { FunctionComponent } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'

import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { CENTER_OF_EXCELLENCE } from '@lyrahealth-inc/shared-app-logic/src/features/centerOfExcellence/types'
import { LYRA_CONFIDENTIALITY_MSG } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { getBreakpointValue } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/breakpoints'

import { IS_WEB } from '../../constants'
import { useGetResponsiveAssets } from '../../hooks/useGetResponsiveAssets'
import { ConfidentialityNotice } from '../../molecules/confidentialityNotice/ConfidentialityNotice'
import { TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { BodyText, Size as BodyTextSize } from '../bodyText/BodyText'
import { ArrowIcon } from '../icons/ArrowIcon'
import { ChevronIconDirection } from '../icons/ChevronIcon'
import { ChevronV2Icon } from '../icons/ChevronV2Icon'
import { Image } from '../image/Image'
import { PressableOpacity } from '../pressableOpacity/PressableOpacity'
import { Title } from '../title/Title'

export interface FeaturedToolkitProps {
  centerOfExcellenceType: CENTER_OF_EXCELLENCE
  isUserTeen: boolean
  isUserInternational?: boolean
  onPress: () => void
  companyName?: string
  programEndDate?: string
  isFeatured?: boolean
  isCarousel?: boolean
}

const neurodiversityMessages = defineMessages({
  title: {
    defaultMessage: 'Learn more about neurodiversity support',
    description: 'Title for the neurodiversity center of excellence entrypoint',
  },
  adultDescription: {
    defaultMessage:
      'Expert care for neurodiverse individuals, parents, and children ages {isUserInternational, select, true {6-17} other {1-17}}, including neuropsychological evaluations and caregiver coaching.',
    description: 'Description for the neurodiversity center of excellence entrypoint for adult users',
  },
  teenDescription: {
    defaultMessage: 'Access expert care for neurodiverse needs, consider neuropsychological testing, and more.',
    description: 'Description for the neurodiversity center of excellence entrypoint for teen users',
  },
  imageDescription: {
    defaultMessage: 'Neurodiversity support',
    description: 'Alt text for neurodiversity entrypoint banner image',
  },
})

const managerCoachingMessages = defineMessages({
  title: {
    defaultMessage: '{companyName} invited you to manager growth coaching',
    description: 'Title for the featured Manager Coaching entrypoint',
  },
  description: {
    defaultMessage:
      'Learn to lead with confidence in this personalized program{hasEndDate, select, true { before it ends on {programEndDate}} other {}}',
    description: 'Description for the featured Manager Coaching entrypoint',
  },
  imageDescription: {
    defaultMessage: 'Manager growth coaching',
    description: 'Alt text for Manager Coaching entrypoint banner image',
  },
})

const StyledPressableOpacity = styled(PressableOpacity)<{ isCarousel: boolean }>(({ isCarousel }) => ({
  maxWidth: '944px',
  ...(isCarousel && { flexGrow: 1, minHeight: 378 }),
}))

const Container = styled.View<{ isFeatured: boolean; isCarousel: boolean }>(({ theme, isFeatured, isCarousel }) => ({
  ...(isCarousel && { flexGrow: 1 }),
  backgroundColor: theme.colors.cardBackgroundPrimary,
  borderRadius: getBreakpointValue(theme.breakpoints, {
    isMobileSized: theme.spacing['24px'],
    default: isFeatured ? theme.spacing['16px'] : theme.spacing['24px'],
  }),
  ...(isFeatured && { width: '100%' }),
  flexDirection: getBreakpointValue(theme.breakpoints, { isMobileSized: 'column', default: 'row' }),
  overflow: 'hidden',
  maxWidth: '944px',
}))

const ImageContainer = styled.View<{ isFeatured: boolean }>(({ theme, isFeatured }) => ({
  flexShrink: 0,
  overflow: 'hidden',
  position: 'relative',
  ...getBreakpointValue<CSSObject>(theme.breakpoints, {
    isMobileSized: { width: '100%', height: isFeatured ? '159px' : '180px' },
    isMinWidthTablet: { width: '304px', minHeight: '144px' },
    default: { width: '200px', minHeight: '144px' },
  }),
}))

const ContentContainer = styled.View<{ isFeatured: boolean; isCarousel: boolean }>(
  ({ theme, isFeatured, isCarousel }) => ({
    ...(isCarousel && { flexGrow: 1 }),
    gap: theme.spacing['8px'],
    ...getBreakpointValue<CSSObject>(theme.breakpoints, {
      isMobileSized: {
        flexDirection: isFeatured ? 'column' : 'row',
        alignItems: isFeatured ? 'stretch' : 'center',
        padding: isFeatured ? theme.spacing['16px'] : theme.spacing['24px'],
      },
      default: { flex: 1, flexDirection: 'row', alignItems: 'center', padding: theme.spacing['24px'] },
    }),
  }),
)

const TextContainer = styled.View<{ isFeatured: boolean; isCarousel: boolean }>(
  ({ theme, isFeatured, isCarousel }) => ({
    flexDirection: 'column',
    ...getBreakpointValue<CSSObject>(theme.breakpoints, {
      isMobileSized: {
        ...(isFeatured ? { flexGrow: isCarousel ? 1 : 0 } : { flex: 1 }),
        gap: theme.spacing['8px'],
      },
      default: { flex: 1, gap: isFeatured ? theme.spacing['4px'] : theme.spacing['8px'] },
    }),
  }),
)

const ChevronContainer = styled.View({
  justifyContent: 'center',
  alignItems: 'center',
})

const ImageFill = styled.View({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
})

const StyledImage = styled(Image)({
  width: '100%',
  height: '100%',
  ...(IS_WEB && { objectFit: 'cover' }),
})

const FeaturedArrow = styled.View<{ isCarousel: boolean }>(({ theme, isCarousel }) => ({
  alignItems: 'flex-end',
  marginTop: isCarousel ? 'auto' : theme.spacing['4px'],
}))

const ConfidentialityNoticeContainer = styled.View(({ theme }) => ({
  marginTop: getBreakpointValue(theme.breakpoints, {
    isMobileSized: theme.spacing['4px'],
    default: theme.spacing['12px'],
  }),
}))

export const FeaturedToolkit: FunctionComponent<FeaturedToolkitProps> = ({
  centerOfExcellenceType,
  isUserTeen,
  isUserInternational = false,
  onPress,
  companyName,
  programEndDate,
  isFeatured = false,
  isCarousel = false,
}) => {
  const {
    assets: {
      neurodiversityEntrypointBannerAdult,
      neurodiversityEntrypointBannerTeen,
      managerCoachingFeaturedToolkit,
      managerCoachingFeaturedToolkitCarousel,
    },
    breakpoints: { isMobileSized, isMinWidthTablet },
    colors,
  } = useTheme() as ThemeType
  const { formatMessage, formatDate } = useIntl()
  const contentByToolkit = {
    [CENTER_OF_EXCELLENCE.NEURODIVERSITY]: {
      title: neurodiversityMessages.title,
      description: isUserTeen ? neurodiversityMessages.teenDescription : neurodiversityMessages.adultDescription,
      image: isUserTeen ? neurodiversityEntrypointBannerTeen : neurodiversityEntrypointBannerAdult,
      imageDescription: neurodiversityMessages.imageDescription,
      showConfidentialityNotice: false,
    },
    [CENTER_OF_EXCELLENCE.MANAGER_HUB]: {
      title: managerCoachingMessages.title,
      description: managerCoachingMessages.description,
      image: isCarousel ? managerCoachingFeaturedToolkitCarousel : managerCoachingFeaturedToolkit,
      imageDescription: managerCoachingMessages.imageDescription,
      showConfidentialityNotice: true,
    },
  }
  const { title, description, image, imageDescription, showConfidentialityNotice } =
    contentByToolkit[centerOfExcellenceType as keyof typeof contentByToolkit]

  const imageUrl = useGetResponsiveAssets(image?.assetNames ?? { defaultAsset: '' })
  const titleValues = { companyName }
  const descriptionValues = {
    isUserInternational,
    hasEndDate: Boolean(programEndDate),
    programEndDate: programEndDate
      ? formatDate(programEndDate, { month: 'numeric', day: 'numeric', year: '2-digit', timeZone: 'UTC' })
      : '',
  }

  return (
    <StyledPressableOpacity
      isCarousel={isCarousel}
      onPress={onPress}
      testID={tID('FeaturedToolkit')}
      accessibilityLabel={formatMessage(title, titleValues)}
    >
      <Container isFeatured={isFeatured} isCarousel={isCarousel}>
        <ImageContainer isFeatured={isFeatured}>
          {imageUrl ? (
            <ImageFill>
              <StyledImage source={{ uri: imageUrl }} alt={formatMessage(imageDescription)} contentFit='cover' />
            </ImageFill>
          ) : null}
        </ImageContainer>
        <ContentContainer isFeatured={isFeatured} isCarousel={isCarousel}>
          <TextContainer isFeatured={isFeatured} isCarousel={isCarousel}>
            <Title
              level={isFeatured ? '3' : undefined}
              size={isFeatured ? TitleSize.SMALL : isMinWidthTablet ? TitleSize.MEDIUM : TitleSize.SMALL}
              text={formatMessage(title, titleValues)}
              color={colors.textPrimary}
              numberOfLines={isFeatured ? undefined : isMinWidthTablet ? 2 : 4}
            />
            <BodyText
              size={isFeatured ? BodyTextSize.SMALL : undefined}
              text={formatMessage(description, descriptionValues)}
              color={isFeatured ? colors.textPrimary : colors.textSecondary}
            />
            {showConfidentialityNotice && (
              <ConfidentialityNoticeContainer>
                <ConfidentialityNotice
                  omitMobileSpacing
                  text={<FormattedMessage {...LYRA_CONFIDENTIALITY_MSG} />}
                  accessibilityLabel={formatMessage(LYRA_CONFIDENTIALITY_MSG)}
                />
              </ConfidentialityNoticeContainer>
            )}
            {isFeatured && isMobileSized && (
              <FeaturedArrow isCarousel={isCarousel}>
                <ArrowIcon fillColor={colors.textPrimary} />
              </FeaturedArrow>
            )}
          </TextContainer>
          {!(isFeatured && isMobileSized) && (
            <ChevronContainer>
              {isFeatured ? (
                <ArrowIcon fillColor={colors.textPrimary} />
              ) : (
                <ChevronV2Icon direction={ChevronIconDirection.RIGHT} fillColor={colors.textPrimary} size={12} />
              )}
            </ChevronContainer>
          )}
        </ContentContainer>
      </Container>
    </StyledPressableOpacity>
  )
}
